import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { PackageCheck, LayoutDashboard, FileSpreadsheet, Mail, Scale, Users, Wallet, Palmtree, Gift, FolderOpen, BarChart3, MessageSquareText, UserSearch, FileBarChart, Settings, ListTodo, Building2, CalendarCheck, AlertTriangle, Archive, KeyRound, Plug, UserPlus, UserMinus, HeartPulse, Sparkles, Bell, Search, LogOut, Menu, ChevronsUpDown, FileStack, FileSignature, CalendarDays, Target, Megaphone, Landmark, Handshake } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { AssistantSheet } from "./AssistantSheet";
import { DemoBadge } from "./common";
import { dateTimeBR } from "../lib/format";
import { AssistantProvider } from "./voice/AssistantContext";
import { AssistantMount } from "./voice/FloatingAssistant";

const NAV = {
  empresa: [
    ["Início", "/empresa", LayoutDashboard, "dashboard.view"], ["Funcionários", "/empresa/funcionarios", Users, "employees.view"], ["Admissões", "/empresa/admissoes", UserPlus, "employees.view"], ["Desligamentos", "/empresa/desligamentos", UserMinus, "employees.view"],
    ["Folha", "/empresa/folha", Wallet, "payroll.view"],
    ["Férias", "/empresa/ferias", Palmtree, "vacations.view"], ["Benefícios", "/empresa/beneficios", Gift, "benefits.view"],
    ["Solicitações", "/empresa/solicitacoes", MessageSquareText, "requests.view"],
    ["Documentos", "/empresa/documentos", FolderOpen, "documents.view"], ["Arquivos e importações", "/empresa/arquivos", FileStack, "documents.view"], ["Indicadores", "/empresa/indicadores", BarChart3, "payroll.view"],
    ["Recrutamento", "/empresa/recrutamento", UserSearch, "dashboard.view", true],
    ["Relatórios", "/empresa/relatorios", FileBarChart, "dashboard.view", true], ["Configurações", "/empresa/configuracoes", Settings, "dashboard.view"],
  ],
  backoffice: [
    ["Início — Central de Clientes", "/backoffice", LayoutDashboard],
    ["Folhas do mês", "/backoffice/folhas", FileSpreadsheet],
    ["Visão geral", "/backoffice/visao-geral", LayoutDashboard, null, false, "Operação"],
    ["Calendário e prazos", "/backoffice/calendario", CalendarDays],
    ["Arquivadas", "/backoffice/arquivadas", Archive, "admin.access"],
    ["Leads comerciais", "/backoffice/leads", Target, "leads.manage", false, "Comercial"],
    ["Indicações", "/backoffice/indicacoes", Handshake, "leads.manage", false, ""],
    ["Central Financeira", "/backoffice/financeiro", Landmark, "finance.manage", false, "Financeiro"],
    ["Carteira", "/backoffice/admin/carteira", Scale, "admin.access", false, "Administração"], ["Empresas (cadastro)", "/backoffice/admin/empresas", Building2, "admin.access"], ["Usuários", "/backoffice/admin/usuarios", Users, "admin.access"], ["Fila de e-mails", "/backoffice/admin/emails", Mail, "admin.access"],
    ["Permissões", "/backoffice/admin/permissoes", KeyRound, "admin.access"], ["Integrações e auditoria", "/backoffice/admin/integracoes", Plug, "admin.access"], ["Categorias de arquivos", "/backoffice/admin/categorias-arquivos", FileStack, "admin.access"], ["Modelos de contrato", "/backoffice/admin/modelos-contrato", FileSignature, "admin.access"], ["Landing page", "/backoffice/admin/landing", Megaphone, "admin.access"],
  ],
};
const ENV_LABEL = { empresa: "Portal da Empresa", backoffice: "DPdigi — Backoffice" };

function Sidebar({ env, onNav }) {
  const { can } = useAuth();
  const loc = useLocation();
  const items = NAV[env].filter((i) => !i[3] || can(i[3]));
  return (
    <nav className="flex flex-col gap-0.5 px-3">
      {items.map(([label, to, Icon, , soon, group]) => {
        const active = to.includes("?") ? loc.pathname + loc.search === to : (to === `/${env}` || to.endsWith("/admin") ? loc.pathname === to : loc.pathname.startsWith(to)) && !(loc.search.includes("type=") && to.endsWith("fila")) && !(loc.search.includes("tab=") && to.endsWith("fila"));
        return (
          <div key={to}>{group && <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-slate-400" data-testid={`nav-group-${group}`}>{group}</div>}
          <NavLink to={to} onClick={onNav} data-testid={`nav-${to.replace(/[/?=,]/g, "-")}`}
            className={`group flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] transition-colors ${active ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
            <Icon className={`h-4 w-4 ${active ? "text-indigo-600" : "text-slate-400 group-hover:text-slate-600"}`} />
            <span className="flex-1">{label}</span>
            {soon && <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">em breve</span>}
          </NavLink></div>
        );
      })}
    </nav>
  );
}

function EnvSwitcher({ env }) {
  const { can, isCompanyUser } = useAuth();
  const nav = useNavigate();
  const envs = (isCompanyUser ? [["empresa", "Empresa", "dashboard.view"]] : [["backoffice", "Backoffice DP", "backoffice.access"], ["empresa", "Ver portal do cliente", "dashboard.view"]])
    .concat(isCompanyUser ? [["backoffice", "Backoffice", "backoffice.access"]] : []).filter((e) => can(e[2]));
  if (envs.length < 2) return null;
  return (
    <div className="hidden md:flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="env-switcher">
      {envs.map(([k, l]) => (
        <button key={k} data-testid={`env-switcher-${k}`} onClick={() => nav(`/${k}`)} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${env === k ? "bg-white shadow-sm text-slate-900 font-medium" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
      ))}
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  const { setActiveCompany, isCompanyUser } = useAuth();
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const go = (path) => { setQ(""); setRes(null); nav(path); };
  const total = res ? res.employees.length + res.requests.length + res.documents.length + res.companies.length : 0;
  return (
    <div className="relative flex-1 max-w-md">
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <Input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar funcionário, CPF, protocolo, documento..." className="pl-9 bg-slate-50 border-slate-200 h-9" />
      {res && (
        <div data-testid="global-search-results" className="absolute top-11 left-0 right-0 bg-white border rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto p-2 text-sm">
          {total === 0 && <div className="p-3 text-slate-500">Nada encontrado com suas permissões.</div>}
          {res.companies.map((c) => <button key={c.id} onClick={() => { go(`/backoffice/operacional/${c.id}`); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50"><span className="label-xs mr-2">Empresa</span>{c.trade_name}</button>)}
          {res.employees.map((e) => <button key={e.id} onClick={() => { if (!isCompanyUser) setActiveCompany({ id: e.company_id }); go(`/empresa/funcionarios/${e.id}`); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 flex justify-between"><span><span className="label-xs mr-2">Funcionário</span>{e.name}</span><span className="font-mono text-xs text-slate-400">{e.cpf}</span></button>)}
          {res.requests.map((r) => <button key={r.id} onClick={() => go(isCompanyUser ? `/empresa/solicitacoes?id=${r.id}` : `/backoffice/fila?id=${r.id}`)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50"><span className="label-xs mr-2">Protocolo</span><span className="font-mono text-xs text-indigo-700">#{r.protocol}</span> {r.title}</button>)}
          {res.documents.map((d) => <button key={d.id} onClick={() => go("/empresa/documentos")} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50"><span className="label-xs mr-2">Documento</span>{d.filename} <span className="text-slate-400">{d.employee_name}</span></button>)}
        </div>
      )}
    </div>
  );
}

export function Notifications() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const unread = items.filter((i) => !i.read).length;
  return (
    <Popover onOpenChange={(o) => { if (!o && unread) api.post("/notifications/read-all").then(load); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-button" className="relative h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center">
          <Bell className="h-4 w-4 text-slate-600" />
          {unread > 0 && <span data-testid="notifications-unread-count" className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-rose-600 text-white text-[10px] flex items-center justify-center">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b text-sm font-semibold">Notificações</div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <div className="p-4 text-sm text-slate-500">Sem notificações.</div>}
          {items.map((n) => (
            <div key={n.id} className={`px-4 py-3 border-b last:border-0 text-sm ${n.read ? "" : "bg-indigo-50/40"}`}>
              <div className="font-medium text-slate-800">{n.title}</div>
              {n.body && <div className="text-xs text-slate-500 mt-0.5">{n.body}</div>}
              <div className="text-[10px] text-slate-400 mt-1 font-mono">{dateTimeBR(n.created_at)}</div>
            </div>
          ))}
        </div>
        <div className="px-4 py-2 border-t text-[10px] text-slate-400">E-mail e WhatsApp: integração pendente</div>
      </PopoverContent>
    </Popover>
  );
}

function CompanyPicker() {
  const { activeCompany, setActiveCompany } = useAuth();
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/backoffice/companies").then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="company-picker-button" className="flex items-center gap-2 text-sm border rounded-lg px-3 h-9 hover:bg-slate-50 max-w-[220px]">
          <Building2 className="h-4 w-4 text-slate-400" /><span className="truncate">{activeCompany?.trade_name || "Selecionar empresa"}</span><ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-1">
        {list.map((c) => <button key={c.id} data-testid={`company-option-${c.id}`} onClick={() => { setActiveCompany({ id: c.id, trade_name: c.trade_name }); window.location.reload(); }} className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 text-sm">{c.trade_name}<div className="text-[11px] text-slate-400">{c.employees_active} funcionários · {c.cnpj}</div></button>)}
      </PopoverContent>
    </Popover>
  );
}

export function CompanyGate() {
  const { setActiveCompany } = useAuth();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/backoffice/companies").then((r) => setList(r.data)); }, []);
  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold">Selecione a empresa</h1>
      <p className="text-sm text-slate-500 mt-1 mb-6">Você está acessando o Portal da Empresa como equipe interna. Escolha qual cliente visualizar.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {(list || []).map((c) => <button key={c.id} data-testid={`gate-company-${c.id}`} onClick={() => setActiveCompany({ id: c.id, trade_name: c.trade_name })} className="card-surface card-hover text-left p-5"><div className="font-semibold">{c.trade_name}</div><div className="text-xs text-slate-500 mt-1">{c.employees_active} funcionários · {c.cnpj}</div></button>)}
      </div>
    </div>
  );
}

export default function AppShell({ env }) {
  const { user, logout, can, isCompanyUser, activeCompany, companyName } = useAuth();
  const [mobile, setMobile] = useState(false);
  const [ai, setAi] = useState(false);
  const nav = useNavigate();
  const needsCompany = env === "empresa" && !isCompanyUser && !activeCompany;
  const side = (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-6 pb-5">
        <img src="/logo.png" alt="DPdigi — Seu Departamento Pessoal Digital" data-testid="brand-logo" className="h-11 w-auto" />
        <div className="text-[10px] text-slate-500 mt-3 uppercase tracking-wider">{ENV_LABEL[env]}</div>
        {env === "backoffice" && <div className="mt-4 text-[10px] leading-snug text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-2 py-1.5">Ambiente interno — invisível ao cliente</div>}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto side-scroll pb-2" data-testid="sidebar-scroll"><Sidebar env={env} onNav={() => setMobile(false)} /></div>
      <div className="mt-auto p-4 border-t">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold">{user.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}</div>
          <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate" data-testid="current-user-name">{user.name}</div><div className="text-[11px] text-slate-500 truncate">{user.role_label}</div></div>
          <button data-testid="logout-button" onClick={async () => { await logout(); nav("/login"); }} className="p-1.5 rounded-md hover:bg-slate-100" title="Sair"><LogOut className="h-4 w-4 text-slate-500" /></button>
        </div>
      </div>
    </div>
  );
  const body = (
    <div className="min-h-screen flex">
      <aside className="hidden lg:block w-64 shrink-0 border-r bg-white sticky top-0 h-screen no-print">{side}</aside>
      <Sheet open={mobile} onOpenChange={setMobile}><SheetContent side="left" className="p-0 w-72" aria-describedby={undefined}><SheetTitle className="sr-only">Menu</SheetTitle>{side}</SheetContent></Sheet>
      <div className="flex-1 min-w-0">
        <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-40 no-print">
          <div className="flex items-center gap-3 px-4 sm:px-8 h-16">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setMobile(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
            {env === "empresa" && !isCompanyUser && <CompanyPicker />}
            {env === "empresa" && isCompanyUser && <div className="hidden md:flex items-center gap-2 text-sm text-slate-600"><Building2 className="h-4 w-4 text-slate-400" />{companyName}</div>}
            <GlobalSearch />
            <div className="ml-auto flex items-center gap-2">
              <EnvSwitcher env={env} />
              {can("ai.use") && env === "empresa" && !needsCompany && (
                <Button data-testid="ai-assistant-toggle-button" size="sm" variant="outline" onClick={() => setAi(true)} className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"><Sparkles className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">DP Assist</span></Button>
              )}
              <Notifications />
            </div>
          </div>
        </header>
        <main className="px-4 sm:px-8 py-8 max-w-[1400px] mx-auto">
          {env === "empresa" && !isCompanyUser && can("backoffice.access") && <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm text-violet-800" data-testid="internal-client-view-banner">
            <span className="flex-1">Você está vendo o <b>Portal Empresa</b>{companyName ? <> de <b>{companyName}</b></> : ""} exatamente como o cliente vê. A tela de trabalho do DP é a Central de Clientes.</span>
            <Button size="sm" onClick={() => nav("/backoffice")} data-testid="internal-client-view-back">Ir para a Central de Clientes</Button></div>}
          {needsCompany ? <CompanyGate /> : <Outlet />}</main>
      </div>
      <AssistantSheet open={ai} onOpenChange={setAi} />
    </div>
  );
  if (env !== "empresa") return body;
  return <AssistantProvider enabled={can("requests.create") && !needsCompany}>{body}<AssistantMount /></AssistantProvider>;
}

export const ComingSoon = ({ title, phase = "Fase 2" }) => (
  <div className="card-surface p-10 max-w-xl" data-testid="coming-soon">
    <div className="flex items-center gap-2 mb-3"><span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 border px-2 py-0.5 rounded-full">{phase}</span><DemoBadge /></div>
    <h1 className="text-2xl font-bold">{title}</h1>
    <p className="text-sm text-slate-500 mt-2">Este módulo está planejado e a estrutura de dados já foi prevista. Ele será liberado em uma próxima etapa — nada aqui simula funcionamento real.</p>
  </div>
);
